import React, { useState } from 'react';
import {
  FAIZ_TURLERI,
  bugunISO,
  hesaplaYasalFaizAraci,
  formatFaizTR,
  formatDateTR,
} from '../utils/faiz';
import './YasalFaizHesap.css';

const emptyForm = () => ({
  anapara: '',
  tur: 'adiKanuni',
  baslangic: '',
  bitis: bugunISO(),
});

const YasalFaizHesap = () => {
  const [form, setForm] = useState(emptyForm);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setForm(emptyForm());
    setResult(null);
    setError('');
  };

  const handleHesapla = (e) => {
    e.preventDefault();
    setError('');
    if (!form.anapara) {
      setError('Anapara zorunludur.');
      return;
    }
    if (!form.baslangic || !form.bitis) {
      setError('Faiz başlangıç ve bitiş tarihleri zorunludur.');
      return;
    }
    if (form.bitis <= form.baslangic) {
      setError('Bitiş tarihi başlangıçtan sonra olmalıdır.');
      return;
    }
    const r = hesaplaYasalFaizAraci(form.anapara, form.baslangic, form.bitis, form.tur);
    if (r.unsupported) {
      setResult(null);
      setError('TÜFE / Yİ-ÜFE hesabı için aylık endeks serisi henüz eklenmedi. Adi kanuni, avans, reeskont veya temerrüt faizini kullanın.');
      return;
    }
    if (!r.toplamGun) {
      setResult(null);
      setError('Bu tarih aralığında faiz hesaplanamadı.');
      return;
    }
    setResult(r);
  };

  return (
    <div className="yasal-faiz-page">
      <div className="yf-header">
        <h1>Yasal Faiz Hesaplama</h1>
        <p>
          3095 sayılı Kanun ve TCMB reeskont / avans oranlarına göre dönemsel faiz.
          31.07.2026 tarihinden itibaren adi kanuni faiz %31’dir.
        </p>
      </div>

      <form className="yf-card" onSubmit={handleHesapla}>
        <div className="yf-badge">Yasal Faiz Hesaplama</div>
        <p className="yf-required-note">* Doldurulması zorunlu alanlar.</p>

        <div className="yf-row">
          <label htmlFor="yf-anapara">* Anapara:</label>
          <div className="yf-field">
            <input
              id="yf-anapara"
              name="anapara"
              className="yf-input"
              value={form.anapara}
              onChange={handleChange}
              placeholder="249370,50"
              inputMode="decimal"
            />
            <span className="yf-hint">TL (Örn. 249370,50)</span>
          </div>
        </div>

        <div className="yf-row">
          <label htmlFor="yf-tur">Faiz Türü:</label>
          <div className="yf-field">
            <select id="yf-tur" name="tur" className="yf-input yf-select" value={form.tur} onChange={handleChange}>
              {FAIZ_TURLERI.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="yf-row">
          <label htmlFor="yf-baslangic">* Faiz Başlangıç Tarihi:</label>
          <div className="yf-field">
            <input
              id="yf-baslangic"
              type="date"
              name="baslangic"
              className="yf-input"
              value={form.baslangic}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="yf-row">
          <label htmlFor="yf-bitis">* Faiz Bitiş Tarihi:</label>
          <div className="yf-field">
            <input
              id="yf-bitis"
              type="date"
              name="bitis"
              className="yf-input"
              value={form.bitis}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {error && <div className="yf-error">{error}</div>}

        <div className="yf-actions">
          <button type="submit" className="yf-btn yf-btn-primary">Hesapla</button>
          <button type="button" className="yf-btn yf-btn-secondary" onClick={handleReset}>Sıfırla</button>
        </div>
      </form>

      {result && (
        <div className="yf-card yf-results">
          <h2>Hesaplama Sonuçları</h2>
          <dl className="yf-summary">
            <div>
              <dt>Faiz İşletilen Süre</dt>
              <dd>{result.sure.text}</dd>
            </div>
            <div>
              <dt>Anapara</dt>
              <dd>{formatFaizTR(result.anapara)} TL</dd>
            </div>
            <div>
              <dt>İşleyen Faiz Tutarı</dt>
              <dd>{formatFaizTR(result.faiz)} TL</dd>
            </div>
            <div className="yf-total">
              <dt>Anapara + Faiz Toplamı</dt>
              <dd>{formatFaizTR(result.toplam)} TL</dd>
            </div>
            <div>
              <dt>Gerçekleşen Toplam Faiz Oranı</dt>
              <dd>%{formatFaizTR(result.gerceklesenOran, 12)}</dd>
            </div>
          </dl>

          <h3>Faiz Hesaplama Detayları</h3>
          <p className="yf-detail-intro">
            {result.tur.label} — anapara × yıllık oran × gün / 365. Oran değişince dönemlere ayrılır.
          </p>
          <div className="yf-table-wrap">
            <table className="yf-table">
              <thead>
                <tr>
                  <th>Dönem</th>
                  <th>Gün</th>
                  <th>Yıllık Oran</th>
                  <th>Uygulanan %</th>
                  <th>Faiz</th>
                </tr>
              </thead>
              <tbody>
                {result.segmentler.map((s, idx) => (
                  <tr key={`${s.baslangic}-${idx}`}>
                    <td>
                      {formatDateTR(s.baslangic)} – {formatDateTR(s.bitisDahilMi ? s.bitisDahil : s.bitisExclusive)}
                      <span className="yf-inc">
                        {s.bitisDahilMi ? ' (bitiş dahil)' : ' (bitiş hariç)'}
                      </span>
                    </td>
                    <td>{s.gun}</td>
                    <td>%{formatFaizTR(s.oran, s.oran % 1 ? 2 : 0)}</td>
                    <td>%{formatFaizTR(s.oranPay, 12)}</td>
                    <td>{formatFaizTR(s.faiz)} TL</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default YasalFaizHesap;
